
import React from 'react';
import { ChangedItem } from '@/types/quick-compare';
import { formatCurrency } from '@/utils/estimates/formatCurrency';
import { cn } from '@/lib/utils';

interface ComparisonItemCardProps {
  item: ChangedItem;
}

export const ComparisonItemCard: React.FC<ComparisonItemCardProps> = ({ item }) => {
  // Get the list of fields that changed for this item
  const changedFields = item.changedFields || [];
  
  console.log('Rendering changed item:', { 
    description: item.original.description, 
    changedFields,
    originalPartType: item.original.partType,
    modifiedPartType: item.modified.partType,
    hasPartTypeChanged: changedFields.includes('partType')
  });
  
  // Helper function to determine if a field has changed
  const hasFieldChanged = (field: string): boolean => {
    return changedFields.includes(field);
  };

  // The exact headers as they appear in the PDF
  const headers = [
    'Line', 'Ver', 'Operation', 'Description', 'Qty', 
    'Extended Price $', 'Part Type', 'Labor', 'Type', 'Paint'
  ];
  
  // Map between our data model fields and the PDF column headers
  const fieldToHeader = {
    'lineNumber': 'Line',
    'operationCode': 'Ver',
    'operation': 'Operation',
    'description': 'Description',
    'quantity': 'Qty', 
    'price': 'Extended Price $',
    'partType': 'Part Type',
    'laborHours': 'Labor',
    'laborType': 'Type',
    'paintHours': 'Paint'
  };

  // List of all fields to display in order
  const fieldsToDisplay = [
    'lineNumber', 'operationCode', 'operation', 'description', 
    'quantity', 'price', 'partType', 'laborHours', 'laborType', 'paintHours'
  ];

  // Helper function to get the display value for a field
  const getDisplayValue = (item: any, field: string): string => {
    if (item[field] === undefined || item[field] === null) return '-';
    
    if (field === 'price') {
      return formatCurrency(item[field]);
    }
    
    if (field === 'laborHours' && typeof item[field] === 'number') {
      return item[field].toFixed(1); // Always show one decimal place
    }
    
    if (field === 'partType') {
      // Make sure part types are always displayed, even if empty or OEM
      const partType = item[field]?.toString() || '';
      return partType === '' ? '-' : partType;
    }
    
    return item[field].toString();
  };
  
  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="p-3 bg-muted">
        <h3 className="font-medium">
          {item.original.description}
          {item.fuzzyMatch && (
            <span className="text-xs text-muted-foreground ml-2">
              (Fuzzy match, {Math.round(item.similarity! * 100)}% similarity)
            </span>
          )}
        </h3>
        {item.original.section && (
          <p className="text-xs text-muted-foreground">
            Section: {item.original.section}
          </p>
        )}
      </div>

      {/* Table styled to match PDF format */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50">
              {headers.map((header) => (
                <th key={header} className="py-2 px-3 text-left font-medium text-muted-foreground">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {/* Original item row */}
            <tr>
              {fieldsToDisplay.map((field) => {
                const value = getDisplayValue(item.original, field);
                return (
                  <td key={`original-${field}`} className="py-2 px-3">
                    {value}
                  </td>
                );
              })}
            </tr>
            
            {/* Modified item row - with highlighted changed cells */}
            <tr className="bg-muted/20">
              {fieldsToDisplay.map((field) => {
                const value = getDisplayValue(item.modified, field);
                const isChanged = hasFieldChanged(field);
                // Use red for decreased values, green for increased values (for numeric fields)
                let textColorClass = '';
                
                if (isChanged && ['price', 'quantity', 'laborHours'].includes(field)) {
                  const originalValue = Number(item.original[field as keyof typeof item.original] || 0);
                  const modifiedValue = Number(item.modified[field as keyof typeof item.modified] || 0);
                  
                  if (!isNaN(originalValue) && !isNaN(modifiedValue)) {
                    // For auto body shops, decreases are unfavorable (red), increases are favorable (green)
                    textColorClass = modifiedValue < originalValue ? 'text-red-600' : 'text-green-600';
                  }
                }
                
                return (
                  <td 
                    key={`modified-${field}`} 
                    className={cn(
                      "py-2 px-3",
                      isChanged && "bg-amber-50",
                      textColorClass
                    )}
                  >
                    {value}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
